import { fromZonedTime, toZonedTime, formatInTimeZone } from "date-fns-tz";
export var Day;
(function (Day) {
    Day[Day["SUNDAY"] = 0] = "SUNDAY";
    Day[Day["MONDAY"] = 1] = "MONDAY";
    Day[Day["TUESDAY"] = 2] = "TUESDAY";
    Day[Day["WEDNESDAY"] = 3] = "WEDNESDAY";
    Day[Day["THURSDAY"] = 4] = "THURSDAY";
    Day[Day["FRIDAY"] = 5] = "FRIDAY";
    Day[Day["SATURDAY"] = 6] = "SATURDAY";
})(Day || (Day = {}));
export const DayNames = {
    [Day.MONDAY]: "Mon",
    [Day.TUESDAY]: "Tue",
    [Day.WEDNESDAY]: "Wed",
    [Day.THURSDAY]: "Thu",
    [Day.FRIDAY]: "Fri",
    [Day.SATURDAY]: "Sat",
    [Day.SUNDAY]: "Sun",
};
export const EMPTY_EVENT = {
    user: "",
    name: "",
    location: "",
    description: "",
    duration: 0,
    isActive: false,
    url: "",
    bufferafter: 0,
    bufferbefore: 0,
    available: {
        [Day.MONDAY]: [],
        [Day.TUESDAY]: [],
        [Day.WEDNESDAY]: [],
        [Day.THURSDAY]: [],
        [Day.FRIDAY]: [],
        [Day.SATURDAY]: [],
        [Day.SUNDAY]: [],
    },
    minFuture: 2 * 86400,
    maxFuture: 60 * 86400,
    maxPerDay: 2,
    availabilityMode: 'define',
    recurrence: {
        enabled: false,
        frequency: 'biweekly',
        interval: 2,
    }
};
;
export class TimeRange {
    constructor(start, end) {
        if (typeof start == 'string')
            start = new Date(start);
        if (typeof end == 'string')
            end = new Date(end);
        if (start >= end) {
            throw new RangeError('Illegal time interval, start >= end');
        }
        this.start = start;
        this.end = end;
    }
}
export class IntervalSet extends Array {
    constructor(...args) {
        super();
        if (args.length === 0)
            return;
        if (args.length >= 3 && args[0] instanceof Date && args[1] instanceof Date) {
            this.initializeWithSlots(args[0], args[1], args[2], args[3]);
        }
        else if (args.length === 2) {
            this.initializeWithDates(args[0], args[1]);
        }
        else if (args.length === 1 && Array.isArray(args[0])) {
            this.initializeWithArray(args[0]);
        }
        else if (args.length === 1) {
            if (typeof args[0] === 'number')
                return;
            this.push(args[0]);
        }
        else {
            throw new Error('Illegal arguments');
        }
    }
    initializeWithSlots(timeMin, timeMax, slots, timeZone = 'Europe/Berlin') {
        let t = timeMin;
        while (t < timeMax) {
            let zonedT = toZonedTime(t, timeZone);
            let day = zonedT.getUTCDay();
            let s = slots[day];
            if (s) {
                s.forEach((slot) => {
                    const start = this.createDateFromSlot(t, slot.start, timeZone);
                    const end = this.createDateFromSlot(t, slot.end, timeZone);
                    this.push({ start, end });
                });
            }
            t = new Date(t.getTime() + 1000 * 86400);
        }
    }
    createDateFromSlot(baseDate, time, timeZone) {
        const parts = time.split(':');
        let hour = parseInt(parts[0], 10);
        const minute = parts[1] ? parseInt(parts[1], 10) : 0;
        let targetDate = baseDate;
        if (hour === 24 && minute === 0) {
            hour = 0;
            targetDate = new Date(baseDate.getTime() + 1000 * 60 * 60 * 24);
        }
        const hourStr = hour.toString().padStart(2, '0');
        const minuteStr = minute.toString().padStart(2, '0');
        const dateStr = formatInTimeZone(targetDate, timeZone, 'yyyy-MM-dd');
        const dateTimeStr = `${dateStr}T${hourStr}:${minuteStr}:00`;
        const d = fromZonedTime(dateTimeStr, timeZone);
        if (Number.isNaN(d.getTime())) {
            throw new TypeError(`Invalid Date generated from time='${time}', dateTimeStr='${dateTimeStr}', timeZone='${timeZone}'`);
        }
        return d;
    }
    initializeWithDates(start, end) {
        start = this.convertToDate(start);
        end = this.convertToDate(end);
        if (start >= end) {
            throw new RangeError('Illegal time interval, start >= end');
        }
        if (start < end) {
            this.push({ start, end });
        }
    }
    initializeWithArray(arr) {
        arr.forEach((x) => {
            x.start = this.convertToDate(x.start);
            x.end = this.convertToDate(x.end);
            this.push(x);
            return x;
        });
    }
    convertToDate(value) {
        return (typeof value === 'string' || value instanceof String) ? new Date(value.toString()) : value;
    }
    static equals(r1, r2) {
        return (r1.start.getTime() == r2.start.getTime()) && (r1.end.getTime() == r2.end.getTime());
    }
    static overlap(r1, r2) {
        return !(r1.end < r2.start || r2.end < r1.start);
    }
    overlapping(other) {
        let l = [];
        this.forEach((range, index) => {
            if (IntervalSet.overlap(range, other)) {
                l.push(index);
            }
        });
        return l.filter((x) => x !== null);
    }
    addRange(other) {
        let _overlapping = this.overlapping(other);
        if (_overlapping.length == 0) {
            this.push(other);
            this.sort((r1, r2) => r1.start.getTime() - r2.start.getTime());
        }
        else {
            _overlapping.forEach((index) => {
                let current = this[index];
                if (other.start < current.start) {
                    current.start = other.start;
                    other.start = other.end;
                }
                if (other.end > current.end) {
                    current.end = other.end;
                    other.start = other.end;
                }
                if (index > 0) {
                    let last = this[index - 1];
                    if (last.end >= current.start) {
                        last.end = current.end;
                        current.start = current.end;
                    }
                }
            });
            let filtered = this.filter(x => x.start < x.end);
            filtered.sort((r1, r2) => r1.start.getTime() - r2.start.getTime());
            this.splice(0, this.length, ...filtered);
        }
    }
    add(other) {
        let i = 0;
        let j = 0;
        const merged = [];
        while (i < this.length && j < other.length) {
            if (this[i].start.getTime() <= other[j].start.getTime()) {
                merged.push(this[i++]);
            }
            else {
                const o = other[j++];
                merged.push({ start: o.start, end: o.end });
            }
        }
        while (i < this.length) {
            merged.push(this[i++]);
        }
        while (j < other.length) {
            const o = other[j++];
            merged.push({ start: o.start, end: o.end });
        }
        if (merged.length > 0) {
            const result = [];
            let current = merged[0];
            for (let k = 1; k < merged.length; k++) {
                const next = merged[k];
                if (current.end >= next.start) {
                    if (next.end > current.end) {
                        current.end = next.end;
                    }
                }
                else {
                    result.push(current);
                    current = next;
                }
            }
            result.push(current);
            this.splice(0, this.length, ...result);
        }
        return this;
    }
    intersect(other) {
        let result = new IntervalSet();
        let i = 0;
        let j = 0;
        while (i < this.length && j < other.length) {
            let x = this[i];
            let y = other[j];
            let start = x.start > y.start ? x.start : y.start;
            let end = x.end < y.end ? x.end : y.end;
            if (start < end) {
                result.push({ start, end });
            }
            if (x.end < y.end) {
                i++;
            }
            else {
                j++;
            }
        }
        return result;
    }
    inverse(start, end) {
        let result = new IntervalSet();
        const startDate = start ? (typeof start === 'string' ? new Date(start) : start) : undefined;
        const endDate = end ? (typeof end === 'string' ? new Date(end) : end) : undefined;
        if (startDate) {
            if (this.length > 0) {
                if (startDate < this[0].start) {
                    result.push({ start: startDate, end: this[0].start });
                }
            }
            else if (endDate) {
                result.push({ start: startDate, end: endDate });
                return result;
            }
        }
        for (let i = 1; i < this.length; i++) {
            result.push({ start: this[i - 1].end, end: this[i].start });
        }
        if (endDate && this.length > 0) {
            if (this[this.length - 1].end < endDate) {
                result.push({ start: this[this.length - 1].end, end: endDate });
            }
        }
        return result;
    }
}
//# sourceMappingURL=types.js.map